import type { OAuthAuthorizationDetails, OAuthGrant } from "@supabase/supabase-js";
import { authService } from "@/core/auth/services/authService";
import { providerRedirectUrl } from "@/core/routing/externalNavigation";

export const mcpEnabled = import.meta.env.VITE_MCP_ENABLED === "true";
const allowedClientIds = new Set(
  (import.meta.env.VITE_MCP_ALLOWED_CLIENT_IDS ?? "").split(",").map(value => value.trim()).filter(Boolean),
);
export const isSupportedMcpClient = (clientId: string) => mcpEnabled && allowedClientIds.has(clientId);
export const supportsOAuthManagement = () =>
  authService.kind === "web" && window.self === window.top && !window.ReactNativeWebView;

async function oauth() {
  if (!supportsOAuthManagement()) throw new Error("Manage connected apps in your browser.");
  const { supabase } = await import("@/core/auth/services/supabaseService");
  return supabase.auth.oauth;
}

export function authorizationIdFromSearch(search: string): string | null {
  const values = new URLSearchParams(search).getAll("authorization_id");
  return values.length === 1 && /^[a-zA-Z0-9_-]{1,512}$/.test(values[0]) ? values[0] : null;
}

export const oauthService = {
  async getAuthorization(authorizationId: string, userId: string) {
    const { data, error } = await (await oauth()).getAuthorizationDetails(authorizationId);
    if (error) throw error;
    if (!data) throw new Error("The authorization request is unavailable.");
    // Supabase can return only redirect_url for an already-authorized request.
    if (data.redirect_url) {
      return { kind: "redirect" as const, redirectUrl: providerRedirectUrl(data.redirect_url) };
    }
    if (!data.client?.id || !data.client.name || typeof data.scope !== "string" ||
        data.authorization_id !== authorizationId || data.user?.id !== userId) {
      throw new Error("The authorization request is invalid.");
    }
    return { kind: "consent" as const, details: data };
  },
  async decide(authorizationId: string, details: OAuthAuthorizationDetails, approve: boolean) {
    if (approve && !isSupportedMcpClient(details.client.id)) {
      throw new Error("This app is not an approved Froppio MCP client.");
    }
    const api = await oauth();
    const { data, error } = approve
      ? await api.approveAuthorization(authorizationId, { skipBrowserRedirect: true })
      : await api.denyAuthorization(authorizationId, { skipBrowserRedirect: true });
    if (error) throw error;
    if (!data?.redirect_url) throw new Error("The authorization server did not return a destination.");
    return providerRedirectUrl(data.redirect_url);
  },
  async listGrants(): Promise<OAuthGrant[]> {
    const { data, error } = await (await oauth()).listGrants();
    if (error) throw error;
    if (!Array.isArray(data)) throw new Error("Connected apps are unavailable.");
    return data;
  },
  async revokeGrant(clientId: string) {
    const { error } = await (await oauth()).revokeGrant({ clientId });
    if (error) throw error;
  },
};
